import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { Calendar, Users, Loader2, CheckCircle2, XCircle, Clock, Pencil, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/reservations")({
  head: () => ({ meta: [{ title: "Rezervasyonlarım — JUËN" }] }),
  component: MyReservationsPage,
});

type Status = "pending" | "confirmed" | "rejected" | "cancelled";

interface Row {
  id: string;
  event_id: string;
  party_size: number;
  preferred_date: string;
  notes: string | null;
  status: Status;
  admin_notes: string | null;
  created_at: string;
  event?: { name: string; starts_at: string } | null;
}

function useStatusConfig(): Record<Status, { label: string; className: string; Icon: typeof Clock }> {
  const { t } = useI18n();
  return {
    pending: { label: t("account.reservations.status.pending"), className: "bg-amber-500/15 text-amber-600 dark:text-amber-400", Icon: Clock },
    confirmed: { label: t("account.reservations.status.confirmed"), className: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400", Icon: CheckCircle2 },
    rejected: { label: t("account.reservations.status.rejected"), className: "bg-destructive/15 text-destructive", Icon: XCircle },
    cancelled: { label: t("account.reservations.status.cancelled"), className: "bg-muted text-muted-foreground", Icon: XCircle },
  };
}

function MyReservationsPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const STATUS = useStatusConfig();
  const [rows, setRows] = useState<Row[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [savingId, setSavingId] = useState<string | null>(null);

  const startEdit = (r: Row) => {
    setEditingId(r.id);
    setEditValue(String(r.party_size));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const saveEdit = async (id: string) => {
    const size = Number.parseInt(editValue, 10);
    if (!Number.isFinite(size) || size < 3 || size > 50) {
      toast.error(t("account.reservations.partySizeError"));
      return;
    }
    setSavingId(id);
    const { error } = await supabase
      .from("reservations")
      .update({ party_size: size })
      .eq("id", id);
    setSavingId(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, party_size: size } : r)));
    setEditingId(null);
    toast.success(t("account.reservations.partySizeUpdated"));
  };

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    void (async () => {
      setLoadingList(true);
      const { data, error } = await supabase
        .from("reservations")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) {
        toast.error(error.message);
        setLoadingList(false);
        return;
      }
      const items = (data ?? []) as Row[];
      const eventIds = Array.from(new Set(items.map((r) => r.event_id)));
      const { data: evs } = eventIds.length
        ? await supabase.from("events").select("id,name,starts_at").in("id", eventIds)
        : { data: [] as any[] };
      const eMap = new Map((evs ?? []).map((e: any) => [e.id, e]));
      setRows(items.map((r) => ({ ...r, event: eMap.get(r.event_id) ?? null })));
      setLoadingList(false);
    })();
  }, [user]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="mb-2 text-3xl font-bold tracking-tight">{t("account.reservations.title")}</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          {t("account.reservations.subtitle")}
        </p>

        {loadingList ? (
          <div className="py-16 text-center text-muted-foreground">
            <Loader2 className="mx-auto h-6 w-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
            {t("account.reservations.empty")}{" "}
            <Link to="/events" className="text-primary underline-offset-4 hover:underline">
              {t("account.reservations.viewEvents")}
            </Link>
            .
          </div>
        ) : (
          <div className="space-y-4">
            {rows.map((r) => {
              const s = STATUS[r.status];
              const Icon = s.Icon;
              return (
                <div key={r.id} className="space-y-3 rounded-2xl border border-border bg-card p-5 shadow-card">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-base font-semibold">{r.event?.name ?? "—"}</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {t("account.reservations.requestedAt")} {new Date(r.created_at).toLocaleString(locale)}
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${s.className}`}>
                      <Icon className="h-3.5 w-3.5" /> {s.label}
                    </span>
                  </div>

                  <div className="grid gap-2 text-sm sm:grid-cols-2">
                    <div className="inline-flex items-center gap-1.5">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      {new Date(r.preferred_date).toLocaleString(locale, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </div>
                    <div className="inline-flex items-center gap-1.5">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      {editingId === r.id ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Input
                            type="number"
                            min={3}
                            max={50}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="h-8 w-20"
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            disabled={savingId === r.id}
                            onClick={() => void saveEdit(r.id)}
                          >
                            {savingId === r.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={cancelEdit}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </span>
                      ) : (
                        <>
                          <span>
                            {t("account.reservations.personMin", { size: r.party_size, amount: (r.party_size * 3000).toLocaleString(locale) })}
                          </span>
                          {r.status === "pending" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => startEdit(r)}
                              aria-label={t("account.reservations.editPartySize")}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {r.notes && (
                    <div className="rounded-lg bg-muted p-3 text-sm">
                      <span className="font-medium">{t("account.reservations.yourNote")}</span> {r.notes}
                    </div>
                  )}

                  {r.status === "rejected" && (
                    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
                      <p className="font-medium text-destructive">{t("account.reservations.rejectedTitle")}</p>
                      {r.admin_notes && (
                        <p className="mt-1 text-muted-foreground">{r.admin_notes}</p>
                      )}
                    </div>
                  )}

                  {r.status === "confirmed" && (
                    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm">
                      <p className="font-medium text-emerald-600 dark:text-emerald-400">
                        {t("account.reservations.confirmedTitle")}
                      </p>
                      <p className="mt-1 text-muted-foreground">
                        {t("account.reservations.confirmedDesc")}
                      </p>
                      {r.admin_notes && (
                        <p className="mt-2 text-foreground">{r.admin_notes}</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
